import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/UI/Typography';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import PersonIcon from '@material-ui/icons/Person';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import {
  CheckCircle,
  ChevronRight,
  CreditCard,
  Timer,
  CloudUpload,
} from '@material-ui/icons';
import Icon from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Icon';
import PiggyBank from 'Icons/PiggyBank';
import CircleChartFilled from 'Icons/CircleChartFilled';
import Scales from 'Icons/Scales';
import history from 'State/history';
import { dashboardUri } from 'Consts/clientUris';
import HomeIcon from '@material-ui/icons/Home';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { noop } from 'Helpers/PropTypes';
import StarIcon from '@material-ui/icons/Star';
import styles from './navigationCardStyles';

const getIconForModule = (moduleName, props) => {
  switch (moduleName) {
    case 'familyInformation':
      return <PersonIcon {...props} />;
    case 'goals':
      return <StarIcon {...props} />;
    case 'ownAndOwe':
    case 'balanceSheet':
      return <Icon icon={CircleChartFilled} {...props} />;
    case 'income':
      return <Icon icon={PiggyBank} {...props} />;
    case 'expenses':
      return <CreditCard {...props} />;
    case 'clientDashboard':
      return <HomeIcon {...props} />;
    case 'riskTolerance':
      return <Scales {...props} />;
    case 'investWithFacet':
      return <EqualizerIcon {...props} />;
    case 'documentUpload':
      return <CloudUpload {...props} />;
    default:
      return <PersonIcon {...props} />;
  }
};

const NavigationCard = ({
  className,
  classes,
  resource,
  toggleDrawer,
  onClick,
  compact,
  ...rest
}) => {
  const isComplete = resource.attr('isComplete') === 'true';
  const isInProgress = resource.attr('isInProgress') === 'true';
  const isNextUp = resource.attr('isNextUp') === 'true';
  const isOnboardingStep = resource.attr('isOnboardingStep') === 'true';
  const hasNextLink = !!resource.link('next');
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const goToDashboard = () => history.push(dashboardUri);

  const handleClick = async () => {
    if (!hasNextLink) return;

    if (isOnboardingStep) {
      await onClick(resource.link('next'));
    }

    const shouldGoToDashboard = resource.id() === 'clientDashboard';
    if (shouldGoToDashboard) {
      goToDashboard();
    }

    logStandardizedEvent(categories.digitalOnboarding, eventTypes.clickButton, 'navigating', {
      eventProperties: {
        description: `navigate to "${resource.attr('moduleName')}"`,
        isOnboardingStep,
        shouldGoToDashboard,
      },
    });

    toggleDrawer();
  };

  let svgIconFillColor = isNextUp ? 'primary' : 'mediumGray';
  if (!hasNextLink) {
    svgIconFillColor = 'primaryGray2';
  }

  return (
    <Paper
      className={classNames(classes.root, {
        [classes.nextUpPaper]: isNextUp,
        [classes.disabledPaper]: (!isNextUp && !isInProgress && !isComplete),
        [classes.clickable]: hasNextLink,
        [classes.compactPaper]: compact,
        [classes.expandedPaper]: !compact,
      }, className)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleClick();
        }
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={handleClick}
      tabIndex={hasNextLink ? 0 : -1}
      {...rest}
    >
      {getIconForModule(resource.id(), {
        className: classNames(classes.icon, {
          [classes.iconNextUp]: isNextUp,
          [classes.iconDisabled]: (!isNextUp && !isComplete),
          [classes.compactIcon]: compact,
        }),
        fill: svgIconFillColor,
        test: `nav-card-icon-${resource.id()}`,
      })}

      <div
        className={classNames(classes.content, {
          [classes.expanded]: !compact,
          [classes.compact]: compact,
        })}
      >
        <div className={classes.textContent}>
          <Typography color="primary" variant="h6" test={`nav-card-title-${resource.id()}`}>{resource.attr('primaryText')}</Typography>
          <Typography weight={'medium'} color="accentBlueMuted2" variant="subtitle2" className={classes.secondaryText}>{resource.attr('secondaryText')}</Typography>
        </div>

        {isInProgress ? (
          <Typography color="primaryGray2" variant="h6" className={classes.inProgress}>{'In Progress'}</Typography>
        ) : null}

        {isOnboardingStep && isComplete ? (
          <CheckCircle color="secondary" test="nav-card-complete" className={classes.check} />
        ) : null}

        {(isOnboardingStep && !isComplete && !isInProgress) ? (
          <Typography variant="h6" color="primaryGray2" className={classes.timer}><Timer fontSize="small" className={classes.timerIcon} />{resource.attr('estimatedDuration')}</Typography>
        ) : null}

        {hasNextLink ? (
          <ChevronRight className={classes.chevron} test={`nav-card-next-link-${resource.id()}`} />
        ) : <span className={classes.spacer} test={`nav-card-disabled-${resource.id()}`} />}
      </div>
    </Paper>
  );
};

NavigationCard.defaultProps = {
  className: '',
  compact: false,
  toggleDrawer: noop,
};

NavigationCard.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
  toggleDrawer: PropTypes.func,
  compact: PropTypes.bool,
};

export default withStyles(styles)(NavigationCard);
